import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import {
  callNumber,
  changeQty,
  db,
  markDone,
  markReady,
  saveItems,
  startOrderForTable,
  voidOrder,
  voidPaidOrder,
  type Order,
} from '../lib/db'
import { ordersApi } from '../lib/api'
import { pushNow } from '../lib/sync'
import { clock, elapsedText, money, qtyToText, QTY_UNIT } from '../lib/money'
import { hasVoidPasscode } from '../lib/passcode'
import { useApp } from '../lib/store'
import { useNow } from '../lib/hooks'
import { PageHead } from '../components/PageHead'
import { PasscodeSheet } from '../components/PasscodeSheet'
import { ClockMark, CupMark, PencilMark, PersonMark } from '../components/StateIcons'
import { ScooterGlyph } from '../components/ScooterIcon'
import { Sheet } from '../components/Sheet'
import { Thumb } from '../components/Thumb'

/** Lý do huỷ, giống hệt màn hình Hoá đơn — cùng một việc thì cùng một danh sách. */
const VOID_REASONS = ['Rung nhầm bill', 'Khách trả lại hàng', 'Khách đổi món', 'Máy in lỗi, in lại']



/**
 * Quầy mang về.
 *
 * Một màn hình riêng, không chen vào sơ đồ bàn nữa. Lý do: thứ xếp hàng ở quầy mang về là
 * <b>đơn</b>, không phải bàn. Nhét mười lượt khách đang đợi vào một ô vuông trên sơ đồ thì
 * chín người vô hình, mà dựng mười cái bàn giả cho mười người là mô hình hoá hàng đợi bằng
 * đồ nội thất.
 *
 * Bên dưới không đổi gì: đơn vẫn gắn vào cái ô "Mang về" như cũ, vẫn đồng bộ, vẫn vào sổ
 * doanh thu như mọi đơn khác. Chỉ chỗ bày ra là khác.
 */

type Stage = 'ordering' | 'making' | 'ready'

const stageOf = (o: Order): Stage =>
  o.status === 'Open' ? 'ordering' : o.readyAt ? 'ready' : 'making'

const STAGE_LABEL: Record<Stage, string> = {
  ordering: 'Đang gọi món',
  making: 'Đang làm',
  ready: 'Chờ khách lấy',
}

/*
 * Hình vẽ thay cho chữ, cùng bộ với sơ đồ bàn.
 *
 * "Đang làm" mang đồng hồ chứ không mang hình người: ở quầy mang về thì thứ đang trôi là
 * thời gian chờ, còn người thì ai cũng đang đứng đó cả. Ly nước là "đồ xong rồi", giống
 * hệt nghĩa của nó trên ô bàn.
 */
const StageMark = ({ stage }: { stage: Stage }) =>
  stage === 'ordering' ? <PencilMark /> : stage === 'making' ? <ClockMark /> : <CupMark />

/*
 * Dùng lại đúng ba lớp màu của ô bàn, không đẻ thêm bộ màu thứ hai.
 *
 * Người bán đã học được ý nghĩa của chúng bên sơ đồ bàn rồi: xám là chưa gọi món, xanh
 * nước biển là đang phục vụ, xanh đặc là cần làm gì đó ngay.
 */
const STAGE_CLASS: Record<Stage, string> = {
  ordering: 'is-idle',
  making: 'is-serving',
  ready: 'is-call',
}

export function TakeawayScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const shop = useApp((s) => s.shop)
  const now = useNow(30_000)

  const [pickedId, setPickedId] = useState<string | null>(null)

  /*
   * Huỷ một hoá đơn đã thu tiền đi qua hai cửa: mã 6 số, rồi tới lý do.
   *
   * Đây là cách duy nhất sửa một đơn mang về đã trả tiền. Không cho sửa món nữa, vì sửa là
   * tờ bill trong tay khách không còn khớp với sổ. Huỷ cả đơn rồi gọi lại đơn mới thì sau
   * này mở sổ ra vẫn đọc được chuyện gì đã xảy ra.
   */
  const [voidStep, setVoidStep] = useState<'pass' | 'reason' | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  /**
   * Mọi đơn mang về chưa xong việc.
   *
   * Đang gọi món, hoặc đã trả tiền mà chưa giao tận tay khách. Chỉ tính đơn của hôm nay —
   * hoá đơn hôm qua thì khách đã cầm về từ lâu.
   */
  const orders = useLiveQuery(
    async () => {
      const slots = await db.diningTables.filter((t) => t.isTakeaway).toArray()
      const ids = new Set(slots.map((t) => t.id))
      if (ids.size === 0) return []

      const dayStart = new Date()
      dayStart.setHours(0, 0, 0, 0)
      const from = dayStart.toISOString()

      const rows = await db.orders.where('status').anyOf('Open', 'Paid').toArray()
      return rows
        .filter((o) => o.tableId && ids.has(o.tableId))
        .filter((o) => o.status === 'Open' || (!o.doneAt && (o.paidAt ?? '') >= from))
        .sort((a, b) => a.openedAt.localeCompare(b.openedAt))
    },
    [],
    [] as Order[],
  )

  const products = useLiveQuery(() => db.products.toArray(), [], [])
  const imageOf = (productId: string | null) =>
    productId ? (products.find((p) => p.id === productId)?.imageKey ?? null) : null

  const picked = orders.find((o) => o.id === pickedId) ?? null

  const waiting = orders.filter((o) => stageOf(o) !== 'ordering').length
  const ready = orders.filter((o) => stageOf(o) === 'ready').length

  const money2 = useMemo(() => orders.reduce((s, o) => s + o.total, 0), [orders])

  /** Khách tiếp theo: một đơn mới hẳn, số gọi riêng. */
  async function newOrder() {
    const slot = await db.diningTables.filter((t) => t.isTakeaway).first()
    if (!slot) {
      say('Quán chưa có ô Mang về — vào Sơ đồ bàn, sửa một ô thành Mang về')
      return
    }
    const o = await startOrderForTable(slot)
    nav(`/order/${o.id}`)
  }

  async function bump(order: Order, itemId: string, delta: number) {
    await saveItems(order.id, changeQty(order.items, itemId, delta * QTY_UNIT))
  }

  async function drop(order: Order, itemId: string) {
    await saveItems(
      order.id,
      order.items.filter((i) => i.id !== itemId),
    )
  }

  async function finish(order: Order) {
    await markDone(order.id)
    say(`Đã giao ${order.customerName ?? `số ${callNumber(order.code)}`}`)
    setPickedId(null)
  }

  async function doVoid(order: Order) {
    const text = reason.trim()
    if (!text) return

    setBusy(true)
    // Ghi lên máy trước rồi mới báo máy chủ: mất mạng giữa buổi vẫn huỷ được, và lần đồng
    // bộ sau đẩy đúng một chuyển trạng thái này lên.
    await voidPaidOrder(order.id, text)
    try {
      await ordersApi.void(order.id, text)
    } catch {
      void pushNow()
    }
    setBusy(false)
    setVoidStep(null)
    setReason('')
    setPickedId(null)
    say(`Đã huỷ đơn ${callNumber(order.code)}`)
  }

  return (
    <div className="screen">
      <div className="board tables-board">
        <PageHead
          to="/"
          icon={<ScooterGlyph />}
          tone="is-clay"
          title="Mang về"
          sub="Đơn khách đang đợi lấy"
        />

        <div className="tables-split">
          <div className="tables-main">
            <div className="zone-row">
              <span className="zone-count is-paid">
                Đang đợi <b className="num">{waiting}</b>
              </span>
              {ready > 0 && (
                <span className="zone-count is-open">
                  Gọi khách <b className="num">{ready}</b>
                </span>
              )}
              <span className="zone-count">
                Tổng <b className="num">{money(money2)}đ</b>
              </span>
            </div>

            <div className="tile-tables">
              {/*
                Nút mở đơn đứng ở ô đầu tiên, không phải cuối danh sách.
                Quầy đông thì danh sách dài ra phía dưới, mà nút này là thứ bấm nhiều nhất
                — để nó trôi xuống cuối là mỗi lượt khách phải cuộn một lần.
              */}
              <button type="button" className="tile-table tile-new" onClick={() => void newOrder()}>
                <span className="tile-new-plus" aria-hidden="true">
                  +
                </span>
                <b className="tile-table-name">Đơn mới</b>
                <small className="tile-seats">Khách tiếp theo</small>
              </button>

              {orders.map((o) => {
                const stage = stageOf(o)
                return (
                  <button
                    key={o.id}
                    type="button"
                    className={`tile-table ${STAGE_CLASS[stage]}${o.id === pickedId ? ' is-picked' : ''}`}
                    onClick={() => setPickedId(o.id)}
                    aria-pressed={o.id === pickedId}
                  >
                    <span className="tile-badge" title={STAGE_LABEL[stage]}>
                      <StageMark stage={stage} />
                      <span className="sr-only">{STAGE_LABEL[stage]}</span>
                    </span>

                    <span className="tile-disc tile-call num" aria-hidden="true">
                      {callNumber(o.code)}
                    </span>

                    <b className="tile-table-name">{o.customerName ?? `Số ${callNumber(o.code)}`}</b>
                    <small className="tile-seats">
                      {qtyToText(o.items.reduce((s, i) => s + i.qtyMilli, 0))} món
                    </small>

                    <span className="tile-money num">
                      {money(o.total)}
                      <em>{elapsedText(o.openedAt, now)}</em>
                    </span>

                    {o.id === pickedId && (
                      <span className="tile-pick" aria-hidden="true">
                        <svg viewBox="0 0 24 24" focusable="false">
                          <path
                            d="M5 12.5 10 17.5 19 7.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    )}
                  </button>
                )
              })}
            </div>

            {orders.length === 0 && (
              <div className="empty">
                <div className="empty-icon" aria-hidden="true">
                  🛵
                </div>
                <p>Chưa có khách nào đợi lấy. Bấm "Đơn mới" khi có người tới đặt.</p>
              </div>
            )}
          </div>

          <aside className="order-pane" aria-label="Đơn mang về đang chọn">
            {!picked ? (
              <div className="pane-empty">
                <div className="empty-icon" aria-hidden="true">
                  👆
                </div>
                <p>Chạm vào một đơn để báo xong hoặc giao cho khách.</p>
              </div>
            ) : (
              <>
                <div className="pane-head">
                  <span className="pane-name">
                    <PersonMark className="glyph-sm" />
                    {picked.customerName ?? 'Khách mang về'}
                  </span>
                  <span className="pane-call num">{callNumber(picked.code)}</span>
                  <span className="pane-seats">{clock(picked.openedAt)}</span>

                  {/*
                    Thu tiền xong là chốt sổ, không sửa món nữa — nên cất luôn cây bút chì
                    thay vì để nó đó rồi báo lỗi khi bấm vào. Sai thì huỷ cả đơn, gọi lại.
                  */}
                  {picked.status === 'Open' && (
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => nav(`/order/${picked.id}`)}
                      aria-label="Sửa đơn"
                      title="Sửa đơn"
                    >
                      ✎
                    </button>
                  )}
                </div>

                {picked.items.length === 0 ? (
                  <div className="pane-empty">
                    <div className="empty-icon" aria-hidden="true">
                      🧾
                    </div>
                    <p>Đơn đã mở nhưng chưa gọi món nào.</p>
                  </div>
                ) : (
                  <div className="pane-lines">
                    {picked.items.map((i) => (
                      <div key={i.id} className="pane-line">
                        <Thumb
                          name={i.name}
                          imageKey={imageOf(i.productId)}
                          className="pane-thumb"
                        />

                        <div className="pane-line-main">
                          <b>
                            {i.name}
                            {i.optionLabel && <em> · {i.optionLabel}</em>}
                          </b>
                          {i.note && <small className="line-note-text">{i.note}</small>}

                          {/* Đơn đã trả tiền thì không sửa món nữa — sửa là lệch tiền đã thu. */}
                          {picked.status === 'Open' ? (
                            <div className="stepper">
                              <button
                                type="button"
                                onClick={() => void bump(picked, i.id, -1)}
                                aria-label={`Bớt ${i.name}`}
                              >
                                −
                              </button>
                              <span className="num">{qtyToText(i.qtyMilli)}</span>
                              <button
                                type="button"
                                onClick={() => void bump(picked, i.id, 1)}
                                aria-label={`Thêm ${i.name}`}
                              >
                                +
                              </button>
                            </div>
                          ) : (
                            <small className="num">{qtyToText(i.qtyMilli)} phần</small>
                          )}
                        </div>

                        <span className="pane-line-total num">{money(i.lineTotal)}</span>

                        {picked.status === 'Open' && (
                          <button
                            type="button"
                            className="pane-drop"
                            onClick={() => void drop(picked, i.id)}
                            aria-label={`Bỏ ${i.name}`}
                            title="Bỏ món"
                          >
                            🗑
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="pane-total">
                  <span>Tổng tiền</span>
                  <b className="num">{money(picked.total)}đ</b>
                </div>

                {picked.status === 'Paid' ? (
                  <>
                    {/*
                      Huỷ đơn nằm riêng một hàng ở trên, nhỏ và nhạt.
                      Hai nút dưới cùng là việc làm mấy chục lần mỗi ngày, còn nút này một
                      tuần dùng một lần mà bấm nhầm là mất một dòng doanh thu — nên nó
                      không được đứng chen vào tầm ngón cái của hai nút kia.
                    */}
                    <button
                      type="button"
                      className="pane-void"
                      onClick={() => {
                        setReason('')
                        setVoidStep(hasVoidPasscode(shop) ? 'pass' : 'reason')
                      }}
                    >
                      Huỷ đơn đã thu tiền
                    </button>

                    <div className="pane-actions">
                      <button
                        type="button"
                        className={`btn ${picked.readyAt ? 'btn-ghost' : 'btn-amber'}`}
                        onClick={() => void markReady(picked.id, !picked.readyAt)}
                      >
                        {picked.readyAt ? 'Chưa xong' : 'Báo xong'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-brand grow"
                        onClick={() => void finish(picked)}
                      >
                        Đã giao khách
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="pane-actions">
                    {picked.items.length === 0 ? (
                      <button
                        type="button"
                        className="btn btn-danger-ghost"
                        onClick={async () => {
                          await voidOrder(picked.id)
                          say('Đã bỏ đơn trống')
                          setPickedId(null)
                        }}
                      >
                        Bỏ đơn
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => nav(`/order/${picked.id}`)}
                      >
                        + Gọi thêm
                      </button>
                    )}
                    {/*
                      Đơn chưa có món thì việc tiếp theo là gọi món, không phải thu tiền.
                      Cây bút chì phía trên cũng mở đúng màn hình ấy, nhưng nó bé bằng đầu
                      ngón tay và nằm tít trên cùng — còn nút này là chỗ mắt đang nhìn và
                      ngón tay đang đặt ngay sau khi mở đơn.
                    */}
                    {picked.items.length === 0 ? (
                      <button
                        type="button"
                        className="btn btn-brand grow"
                        onClick={() => nav(`/order/${picked.id}`)}
                      >
                        Gọi món <span aria-hidden="true">›</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-brand grow"
                        onClick={() => nav(`/pay/${picked.id}`)}
                      >
                        Thu tiền <span aria-hidden="true">›</span>
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </aside>
        </div>
      </div>

      {picked && voidStep === 'pass' && (
        <PasscodeSheet
          title="Huỷ đơn đã thu tiền"
          note={`Đơn ${callNumber(picked.code)} · ${money(picked.total)}đ. Hỏi chủ quán mã 6 số.`}
          onClose={() => setVoidStep(null)}
          onPass={() => setVoidStep('reason')}
        />
      )}

      {picked && voidStep === 'reason' && (
        <Sheet title="Vì sao huỷ" onClose={() => setVoidStep(null)}>
          <p className="sheet-note">
            Đơn {callNumber(picked.code)} · {money(picked.total)}đ. Hoá đơn không bị xoá, chỉ đánh
            dấu đã huỷ kèm lý do — để sau này còn đối chiếu được.
          </p>

          <div className="field">
            <span>Vì sao huỷ</span>
            <div className="chip-row">
              {VOID_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  className="chip"
                  aria-pressed={reason === r}
                  onClick={() => setReason(r)}
                >
                  {r}
                </button>
              ))}
            </div>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Hoặc gõ lý do khác"
              maxLength={200}
            />
          </div>

          <div className="sheet-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setVoidStep(null)}>
              Quay lại
            </button>
            <button
              type="button"
              className="btn btn-danger-ghost grow"
              disabled={busy || !reason.trim()}
              onClick={() => void doVoid(picked)}
            >
              {busy ? 'Đang huỷ…' : 'Huỷ đơn này'}
            </button>
          </div>
        </Sheet>
      )}
    </div>
  )
}
